import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  RegenerativePath,
  RegenerativePathValidationFlags,
} from '../models/regenerative-path.model';
import { RegenerativePathCreate } from '../models/regenerative-path-create.model';
import { Crop, RegenerativeProtocol } from '../../../common/_models';
import { CropService } from '../../../common/_services';

@Injectable()
export class RegenerativePathService {
  private regenerativeService = environment.regenerativeServiceUrl;

  // Keeps track of:

  // All Regenerative Paths
  $regenerativePaths: BehaviorSubject<{ data: RegenerativePath[]; totalCount: number }> =
    new BehaviorSubject<{ data: RegenerativePath[]; totalCount: number }>(null);
  // Details of the Path which the user is looking at
  $currentRegenerativePath: BehaviorSubject<RegenerativePath> =
    new BehaviorSubject<RegenerativePath>(null);
  // The selected protocol in the path creation flow
  selectedProtocolInPathCreation: RegenerativeProtocol = null;

  // Variable that holds info about the path being created
  public currentRegenerativePathCreate = new BehaviorSubject<RegenerativePathCreate>(
    new RegenerativePathCreate()
  );
  // Boolean that manage the buttons for the stepper in the path creation flow
  public isContinueDisabled: boolean = true;

  //? In a new path creation, user needs visibility to only the crops in the baseline year
  public cropsInBaselineYear: Crop[] = [];

  http = inject(HttpClient);
  cropService = inject(CropService);

  /**
   * @description: Get all regenerative paths
   * @returns {Promise<{ data: RegenerativePath[], totalCount: number }>}
   */
  async getRegenerativePaths(): Promise<{ data: RegenerativePath[]; totalCount: number }> {
    const response = await this.http
      .get<{
        data: RegenerativePath[];
        totalCount: number;
      }>(`${this.regenerativeService}api/private/v1/paths`)
      .toPromise();
    this.$regenerativePaths.next(response);
    return response;
  }

  /**
   * @description: Get the whole object of a specific regenerative path by ID
   * @param pathId: {string} the id of the path
   * @returns {Promise<{ data: RegenerativePath[], totalCount: number }>}
   */
  async getPathById(pathId: string): Promise<{ data: RegenerativePath[]; totalCount: number }> {
    return await this.http
      .get<{
        data: RegenerativePath[];
        totalCount: number;
      }>(`${this.regenerativeService}api/private/v1/paths/${pathId}`)
      .toPromise();
  }

  /**
   * @description: Get regenerative paths by protocol ID
   * @param protocolId: {string} the ID of the protocol
   * @param allCompanies: {boolean} flag to indicate whether to include paths from all companies
   * @returns {Promise<{ data: RegenerativePath[], totalCount: number }>}
   */
  async getPathsByProtocolId(
    protocolId: string,
    allCompanies: boolean = false
  ): Promise<{ data: RegenerativePath[]; totalCount: number }> {
    // Start with the base URL
    const params = new URLSearchParams();

    params.append('protocolId', protocolId);
    // Append the 'all' parameter if needed
    if (allCompanies) {
      params.append('all', 'true');
    }

    return await this.http
      .get<{
        data: RegenerativePath[];
        totalCount: number;
      }>(`${this.regenerativeService}api/private/v1/paths/?${params.toString()}`)
      .toPromise();
  }

  /**
   * @description: Create a new regenerative path
   * @returns {Promise<{ data: RegenerativePath[], totalCount: number }>}
   * @param path
   */
  async createPath(
    path: RegenerativePathCreate
  ): Promise<{ data: RegenerativePath[]; totalCount: number }> {
    return await this.http
      .post<{
        data: RegenerativePath[];
        totalCount: number;
      }>(`${this.regenerativeService}api/private/v1/paths`, path)
      .toPromise();
  }

  /**
   * @description: Update an already existing path
   * @returns {Promise<{ data: RegenerativePath[], totalCount: number }>}
   * @param pathId
   * @param path
   * */
  async updateRegenerativePath(
    pathId: string,
    path: RegenerativePath
  ): Promise<{ data: RegenerativePath; totalCount: number }> {
    return await this.http
      .put<{
        data: RegenerativePath;
        totalCount: number;
      }>(`${this.regenerativeService}api/private/v1/paths/${pathId}`, path)
      .toPromise();
  }

  /**
   * @description: Save multiple paths
   * @returns {Promise<RegenerativePath[]>}
   * @param path
   * */
  async savePathsBulk(path: RegenerativePathCreate[]): Promise<RegenerativePath[]> {
    return await this.http
      .post<RegenerativePath[]>(`${this.regenerativeService}api/private/v1/paths`, path)
      .toPromise();
  }

  /**
   * @description: Update multiple already existing paths
   * @returns {Promise<RegenerativePath[]>}
   * @param path
   * */
  async updatePathsBulk(path: RegenerativePath[]): Promise<RegenerativePath[]> {
    return await this.http
      .put<RegenerativePath[]>(`${this.regenerativeService}api/private/v1/paths`, path)
      .toPromise();
  }

  /**
   * @description: Delete a regenerative path by ID
   * @returns {Promise<{}>} - Promise resolving to an empty object
   * @param pathId
   */
  async deletePath(pathId: string): Promise<any> {
    const response = await this.http
      .delete(`${this.regenerativeService}api/private/v1/paths/${pathId}`)
      .toPromise();
    return response || {}; // Ensure it returns an empty object if there's no content
  }

  /**
   * @description: Update the validation flag of a regenerative path
   * @returns {Promise<RegenerativePath[]>}
   * @param pathId the id of the path being updated
   * @param flagsObj the object containing all the flags
   * */
  async updatePathValidationFlag(
    pathId: string,
    flagsObj: RegenerativePathValidationFlags
  ): Promise<{ data: RegenerativePath; totalCount: number }> {
    try {
      const result = await lastValueFrom(
        this.http.post<{ data: RegenerativePath; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths/updateFlagPathValidation/${pathId}`,
          flagsObj
        )
      );

      this.$currentRegenerativePath.next(result.data[0]);
      return result;
    } catch (error) {
      console.error('Failed to update path validation flag:', error);
      return null; // or throw error if you want it handled by the caller
    }
  }

  getCurrentRegenerativePathCrops(): Observable<Crop[]> {
    return this.$currentRegenerativePath.pipe(
      switchMap((path: RegenerativePath) => {
        const pathFieldIds = new Set(path.sections.map((s) => s.fieldId));
        const pathParcelIds = new Set(path.sections.map((s) => s.parcelId).filter((id) => !!id));
        return this.cropService
          .getCrops(undefined, undefined, path.baseYear)
          .pipe(
            map((crops: Crop[]) =>
              crops.filter(
                (crop: Crop) =>
                  pathFieldIds.has(crop.field?.id) || pathParcelIds.has(crop.parcel?.id)
              )
            )
          );
      })
    );
  }
}
